<template>
	<div class="carousel-wrapper" @mouseenter="pauseAutoplay()" @mouseleave="resumeAutoplay()">
		<div :id="'carousel-' + carouselId" :ref="'carousel-' + carouselId" class="owl-carousel">
			<div v-for="(slide, index) in slides" class="image-slide-item">
				<template v-if="isLazyLoadSlide(index)">
					<div class="d-inline">
						<lazy-img
							picture-class="img-cover"
							:image-url="slide.imageUrl"
							:fallback-url="slide.fallbackImageUrl"
							:alt="slide.imageAltText"
							:title="slide.title"
							:role="isSrcSetSlide(slide) ? 'option' : false"
							:srcsets="getSrcsets(slide)">
						</lazy-img>
					</div>
				</template>
				<template v-else-if="isReadyToLoadImage(index)">
					<picture>
						<template v-if="isSrcSetSlide(slide)">
							<source v-for="srcset in getSrcsets(slide, true)" :type="'image/' + srcset.type" :srcset="srcset.srcset.join(',')" />
						</template>
						<template v-else>
							<source :srcset="slide.imageUrl" :type="getSrcType(slide)" />
						</template>
						<img
							class="img-cover no-lazyload"
							:src="getImageUrl(slide)"
							:alt="slide.imageAltText"
							:title="slide.title"
							role="option"
						/>
					</picture>
				</template>
				<div :class="['widget-caption', slide.customClass]">
					<div class="slide-content">
						<span class="slide-headline">{{ slide.name }}</span>
						<p class="slide-textbox" v-html="slide.textbox"></p>
						<a
							v-if="slide.url.length"
							:href="slide.url"
							:title="slide.title"
							class="slide-button"
							v-html="slide.text"
						></a>
					</div>
				</div>

			</div>
		</div>
		<div class="slide-progress" :ref="'slide-progress-' + carouselId"></div>
		<a
			class="left carousel-control carousel-control-prev"
			:ref="'prev-carousel_' + carouselId"
			role="button"
		>
			<span class="fa fa-chevron-left" aria-hidden="true"></span>
			<span class="sr-only">{{ $translate("Ceres::Template.homepageBack") }}</span>
		</a>

		<a
			class="right carousel-control carousel-control-next"
			:ref="'next-carousel_' + carouselId"
			role="button"
		>
			<span class="fa fa-chevron-right" aria-hidden="true"></span>
			<span class="sr-only">{{ $translate("Ceres::Template.homepageNext") }}</span>
		</a>
	</div>
</template>

<script>
import { isDefined, isNullOrUndefined } from "../../../ceres/helper/utils";
import { detectWebP } from "../../../ceres/helper/featureDetect";
import { whenConsented } from "../../helper/whenConsented";

export default {
	name: "slider-carousel",

	props: {
		animationStyle: {
			type: String,
			default: "slide"
		},
		carouselId: {
			type: String,
			default: ""
		},
		slideInterval: {
			type: Number,
			default: 5000
		},
		isAutoslide: {
			type: Boolean,
			default: false
		},
		noMobileAutoslide: {
			type: Boolean,
			default: true
		},
		waitForConsent: {
			type: Boolean,
			default: true
		},
		lazyLoading: {
			type: Boolean,
			default: false
		},
		noLazyLoadingForFirstImage: {
			type: Boolean,
			default: true
		}
	},

    jsonDataFields: [
        "slides"
    ],

	data() {
		return {
			supported: undefined,
			carousel: undefined,
			isPaused: false,
			hasConsentResponse: false
		};
	},

	beforeMount() {
		detectWebP(supported => {
			this.supported = supported;
		});
	},

	mounted() {
		if (this.waitForConsent) {
			whenConsented(
				"",
				() => {
					this.hasConsentResponse = true;
					this.$nextTick(() => {
						this.initCarousel();
					});
				},
				() => {}
			);
		} else {
			this.$nextTick(() => {
				this.initCarousel();
			});
		}
	},

	methods: {
		initCarousel() {
			const $prev = this.$refs["prev-carousel_" + this.carouselId];
			const $next = this.$refs["next-carousel_" + this.carouselId];
			const useAutoplay =
				this.isAutoslide && (!this.noMobileAutoslide || (this.noMobileAutoslide && window.innerWidth > 991));

			this.carousel = this.$refs["carousel-" + this.carouselId];
			this.resetProgressBar();

			const owl = $(this.carousel).owlCarousel({
				autoplay: useAutoplay,
				autoplayTimeout: this.slideInterval,
				autoplayHoverPause: true,
				dots: false,
				items: 1,
				lazyLoad: false,
				loop: true,
				margin: 0,
				mouseDrag: false,
				center: false,
				nav: false,
				smartSpeed: 350,
				onInitialized: this.startProgressBar,
				onTranslate: this.resetProgressBar,
				onTranslated: this.startProgressBar,
				onResized: this.resetAutoplay
			});

			$($prev).click(function() {
				owl.trigger("prev.owl.carousel");
			});
			$($next).click(function() {
				owl.trigger("next.owl.carousel");
			});
		},
		startProgressBar() {
			const owl = $("#" + this.carouselId);

			if (this.isAutoslide && !this.isPaused) {
				if (this.noMobileAutoslide && window.innerWidth <= 991) {
					owl.trigger("stop.owl.autoplay");
					this.resetProgressBar();
				} else {
					const $slideProgress = this.$refs["slide-progress-" + this.carouselId];

					$slideProgress.style.width = "100%";
					$slideProgress.style.transition = "width " + Math.max(this.slideInterval - 200, 0) + "ms";
					$slideProgress.style.transitionTimingFunction = "ease-out";
				}
			} else {
				owl.trigger("stop.owl.autoplay");
			}
		},
		resetProgressBar() {
			const $slideProgress = this.$refs["slide-progress-" + this.carouselId];

			$slideProgress.style.width = "0";
			$slideProgress.style.transition = "width 0s";
		},
		pauseAutoplay() {
			this.isPaused = true;
			$("#" + this.carousel.id).trigger("stop.owl.autoplay");
			this.resetProgressBar();
		},
		resumeAutoplay() {
			this.isPaused = false;
			if (this.isAutoslide && (!this.noMobileAutoslide || (this.noMobileAutoslide && window.innerWidth > 991))) {
				$("#" + this.carousel.id).trigger("play.owl.autoplay");
				this.startProgressBar();
			}
		},
		resetAutoplay() {
			this.pauseAutoplay();

			setTimeout(() => {
				this.resumeAutoplay();
			}, 1);
		},
		getImageUrl(slide) {
			return isDefined(slide.fallbackImageUrl) ? slide.fallbackImageUrl : slide.imageUrl;
		},
		getSrcType(slide, type = "") {
			if (type == "") {
				return slide.imageUrl.split(".").pop() == "webp" ? "image/webp" : null;
			} else {
				return "image/" + type;
			}
		},
		isSrcSetSlide(slide) {
			return (
				isDefined(slide.fileInfo) &&
				isDefined(slide.fileInfo.file) &&
				isDefined(slide.fileInfo.file.types) &&
				slide.fileInfo.file.types.length
			);
		},
		getSrcsets(slide, passCheck = false) {
			if (passCheck || this.isSrcSetSlide(slide)) {
				const fileInfoSrcsets = [];

				slide.fileInfo.file.types.forEach(type => {
					const srcset = [];

					let defaultFilePath = slide.imageUrl.split("/");

					const defaultFileName = defaultFilePath.pop().split(".").slice(0, -1).join(".");

					defaultFilePath = defaultFilePath.join("/");

					if ("widths" in slide.fileInfo && slide.fileInfo.widths[type]) {
						slide.fileInfo.widths[type].forEach(file => {
							srcset.push([
								defaultFilePath + "/" + (isNullOrUndefined(file.name) ? defaultFileName + "_" + file.value + "w" : file.name) + "." + type + " " + file.value + "w"
							]);
						});
					} else {
						srcset.push([ defaultFilePath + "/" + defaultFileName + "." + type]);
					}

					fileInfoSrcsets.push({
						type,
						srcset
					});
				});

				return fileInfoSrcsets;
			} else {
				return [];
			}
		},
		isLazyLoadSlide(index) {
			return this.lazyLoading && !this.waitForConsent && (
				index !== 0 ||
				index !== this.slides.length - 1 ||
				(index === 0 && !this.noLazyLoadingForFirstImage)
			);
		},
		isReadyToLoadImage(index) {
			return index === 0 ||
				!this.waitForConsent ||
				this.hasConsentResponse;
		}
	}
};
</script>
